import { useState, useEffect, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

// ─── Google Material Symbols (loaded via CDN) ────────────────────────────────
const ICON_LINK = "https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap";
const FONT_LINK = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap";

const Icon = ({ name, size = 20, color, fill = 0, weight = 400, style = {} }: any) => (
  <span className="material-symbols-outlined" style={{
    fontSize: size, color, fontVariationSettings: `'FILL' ${fill}, 'wght' ${weight}, 'opsz' ${size}`,
    lineHeight: 1, verticalAlign: "middle", userSelect: "none", ...style,
  }}>{name}</span>
);

// ─── Maghil Design Tokens ────────────────────────────────────────────────────
const C = {
  bg: "#F5F6FA", surface: "#FFFFFF", card: "#FFFFFF", hover: "#F0F1F5",
  accent: "#D23A3B", accentSoft: "rgba(210,58,59,0.08)", accentGlow: "rgba(210,58,59,0.15)",
  green: "#16A34A", greenSoft: "rgba(22,163,74,0.08)",
  red: "#DC2626", redSoft: "rgba(220,38,38,0.08)",
  orange: "#D97706", orangeSoft: "rgba(217,119,6,0.08)",
  blue: "#2563EB", blueSoft: "rgba(37,99,235,0.08)",
  purple: "#7C3AED", purpleSoft: "rgba(124,58,237,0.08)",
  teal: "#0D9488", tealSoft: "rgba(13,148,136,0.08)",
  t1: "#1A1D2E", t2: "#5A6178", t3: "#9CA3B8",
  cardBorder: "#E2E5EC", divider: "#E8EAF0",
  shadow: "0 1px 4px rgba(0,0,0,0.05)",
  sidebarShadow: "2px 0 8px rgba(0,0,0,0.03)",
  topbarShadow: "0 1px 3px rgba(0,0,0,0.04)",
};

const CHANNEL_COLORS = [C.accent, C.green, C.orange, C.blue, C.purple, C.teal];

// ─── Mock Data ───────────────────────────────────────────────────────────────

const channelMixData = [
  { name: "Dine-In", orders: 2180, revenue: 87200, aov: 40.0, color: C.accent },
  { name: "Pickup", orders: 860, revenue: 25800, aov: 30.0, color: C.green },
  { name: "Delivery", orders: 520, revenue: 18200, aov: 35.0, color: C.orange },
  { name: "DoorDash", orders: 340, revenue: 10880, aov: 32.0, color: C.blue },
  { name: "UberEats", orders: 210, revenue: 6930, aov: 33.0, color: C.purple },
  { name: "Grubhub", orders: 108, revenue: 3456, aov: 32.0, color: C.teal },
];

const paymentColors = [C.accent, C.green, C.orange, C.blue, C.purple];

// Advanced / Phase 2+ mock data
const rfmSegments = [
  { name: "Champions", count: 284, pct: 8.4, clv: "$1,240", avgVisits: 22, color: C.accent },
  { name: "Loyal Regulars", count: 612, pct: 18.1, clv: "$680", avgVisits: 14, color: C.green },
  { name: "Promising New", count: 438, pct: 13.0, clv: "$220", avgVisits: 3, color: C.blue },
  { name: "At Risk", count: 356, pct: 10.5, clv: "$520", avgVisits: 9, color: C.orange },
  { name: "Slipping Away", count: 520, pct: 15.4, clv: "$340", avgVisits: 6, color: "#D97706" },
  { name: "Hibernating", count: 680, pct: 20.1, clv: "$120", avgVisits: 2, color: C.t3 },
  { name: "Lost", count: 490, pct: 14.5, clv: "$85", avgVisits: 1, color: "#CBD5E1" },
];

const cohortData = [
  { cohort: "Sep '25", m0: 100, m1: 32, m2: 24, m3: 19, m4: 16, m5: 14 },
  { cohort: "Oct '25", m0: 100, m1: 29, m2: 22, m3: 18, m4: 15, m5: null },
  { cohort: "Nov '25", m0: 100, m1: 34, m2: 26, m3: 20, m4: null, m5: null },
  { cohort: "Dec '25", m0: 100, m1: 28, m2: 21, m3: null, m4: null, m5: null },
  { cohort: "Jan '26", m0: 100, m1: 31, m2: null, m3: null, m4: null, m5: null },
  { cohort: "Feb '26", m0: 100, m1: null, m2: null, m3: null, m4: null, m5: null },
];

const topCustomers = [
  { name: "Michael Chen", visits: 34, spend: "$4,820", lastVisit: "Mar 8", segment: "Champion", channel: "Dine-In" },
  { name: "Sarah Williams", visits: 28, spend: "$3,640", lastVisit: "Mar 9", segment: "Champion", channel: "Dine-In" },
  { name: "James Rodriguez", visits: 22, spend: "$2,860", lastVisit: "Mar 7", segment: "Loyal", channel: "Pickup" },
  { name: "Emily Davis", visits: 19, spend: "$2,470", lastVisit: "Mar 5", segment: "Loyal", channel: "Dine-In" },
  { name: "David Park", visits: 16, spend: "$2,080", lastVisit: "Mar 3", segment: "Loyal", channel: "Delivery" },
  { name: "Lisa Thompson", visits: 14, spend: "$1,820", lastVisit: "Feb 28", segment: "At Risk", channel: "Dine-In" },
  { name: "Robert Kim", visits: 12, spend: "$1,560", lastVisit: "Mar 6", segment: "Loyal", channel: "Pickup" },
  { name: "Anna Martinez", visits: 11, spend: "$1,430", lastVisit: "Mar 1", segment: "Promising", channel: "DoorDash" },
];

// ─── Sidebar Nav Config ──────────────────────────────────────────────────────
const navGroups = [
  { emoji: "📊", label: "Sales", items: ["Sales Summary", "Hourly Sales", "Order Channels"] },
  { emoji: "🍽️", label: "Menu", items: ["Item Sales", "Category Sales", "Discounts & Offers"] },
  { emoji: "👥", label: "Team", items: ["Staff Performance", "Voids & Exceptions", "Refunds"] },
  { emoji: "💰", label: "Payments", items: ["Tender Types", "EOD Reconciliation"] },
  { emoji: "🪑", label: "Guests", items: ["Check-In Analytics", "Party & Wait Times", "Customers"] },
  { emoji: "📍", label: "Locations", items: ["Location Benchmark"] },
];

// ─── Date Presets ────────────────────────────────────────────────────────────
const datePresets = ["Today", "7D", "30D", "This Month", "Last Month", "Quarter"];

// ─── Utility Components ──────────────────────────────────────────────────────

const SectionCard = ({ children, title, subtitle, icon, flex = 1, style = {}, rightAction }: any) => (
  <div style={{
    background: C.card, borderRadius: 14, border: `1px solid ${C.cardBorder}`,
    boxShadow: C.shadow, padding: 22, flex, minWidth: 0, ...style,
  }}>
    {title && (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {icon && <Icon name={icon} size={18} color={C.t3} weight={400} />}
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.t1 }}>{title}</div>
            {subtitle && <div style={{ fontSize: 11, color: C.t3, marginTop: 1 }}>{subtitle}</div>}
          </div>
        </div>
        {rightAction}
      </div>
    )}
    {children}
  </div>
);

const FilterPill = ({ label, active, onClick }: any) => (
  <button onClick={onClick} style={{
    padding: "4px 12px", borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: "pointer",
    border: "none", transition: "all 0.15s",
    background: active ? C.accentSoft : "transparent",
    color: active ? C.accent : C.t2,
  }}>{label}</button>
);

const SegmentBadge = ({ label, color }: any) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px",
    borderRadius: 4, fontSize: 10, fontWeight: 600, color,
    background: `${color}14`,
  }}>
    <span style={{ width: 6, height: 6, borderRadius: 2, background: color }} />
    {label}
  </span>
);

const PhaseTag = ({ phase }: any) => (
  <span style={{
    padding: "2px 6px", borderRadius: 4, fontSize: 9, fontWeight: 600,
    color: C.orange, background: C.orangeSoft, letterSpacing: 0.3,
  }}>PHASE {phase}</span>
);

// Custom tooltip
const CustomTooltip = ({ active, payload, label, prefix = "", suffix = "" }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: C.t1, borderRadius: 8, padding: "8px 12px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    }}>
      <div style={{ fontSize: 10, color: C.t3, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize: 11, color: "#fff", display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ width: 6, height: 6, borderRadius: 2, background: p.color || C.accent }} />
          {p.name}: {prefix}{typeof p.value === "number" ? p.value.toLocaleString() : p.value}{suffix}
        </div>
      ))}
    </div>
  );
};

// ─── Heatmap cell helper ─────────────────────────────────────────────────────
const getCohortColor = (val: any) => {
  if (val === null || val === undefined) return C.hover;
  if (val >= 30) return C.green;
  if (val >= 20) return "rgba(22,163,74,0.55)";
  if (val >= 15) return "rgba(22,163,74,0.35)";
  if (val >= 10) return "rgba(22,163,74,0.2)";
  return "rgba(22,163,74,0.08)";
};

// ─── Main Component ──────────────────────────────────────────────────────────

export default function CustomersReportdesktop() {
  const [activeDate, setActiveDate] = useState("30D");
  const [expandedGroups, setExpandedGroups] = useState(["Guests"]);
  const [activeNav, setActiveNav] = useState("Customers");
  const [compareMode, setCompareMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const toggleGroup = (label: any) => {
    setExpandedGroups(prev =>
      prev.includes(label) ? prev.filter(g => g !== label) : [...prev, label]
    );
  };

  return (
    <>
      <link href={ICON_LINK} rel="stylesheet" />
      <link href={FONT_LINK} rel="stylesheet" />
      <div style={{
        fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        display: "flex", height: "100vh", background: C.bg, color: C.t1, overflow: "hidden",
      }}>

        {/* ════════════ SIDEBAR (250px) ════════════ */}
        <aside style={{
          width: 250, minWidth: 250, height: "100vh", background: C.surface,
          borderRight: `1px solid ${C.divider}`, boxShadow: C.sidebarShadow,
          display: "flex", flexDirection: "column", position: "sticky", top: 0,
          overflow: "hidden",
        }}>
          {/* Logo */}
          <div style={{ padding: "18px 20px", borderBottom: `1px solid ${C.divider}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 10,
                background: "linear-gradient(135deg, #D23A3B, #E86B6B)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontSize: 16, fontWeight: 700,
              }}>M</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.t1 }}>Maghil</div>
                <div style={{ fontSize: 10, color: C.t3, letterSpacing: 0.5 }}>ANALYTICS</div>
              </div>
            </div>
          </div>

          {/* Outlet Selector */}
          <div style={{ padding: "12px 16px" }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 6, padding: "7px 10px",
              background: C.hover, border: `1px solid ${C.cardBorder}`, borderRadius: 8,
              cursor: "pointer",
            }}>
              <Icon name="location_on" size={14} color={C.t3} />
              <span style={{ fontSize: 11, fontWeight: 600, color: C.t1, flex: 1 }}>Downtown Manhattan</span>
              <Icon name="expand_more" size={16} color={C.t3} />
            </div>
          </div>

          {/* Nav Groups */}
          <nav style={{ flex: 1, overflowY: "auto", padding: "4px 0" }}>
            {navGroups.map(group => {
              const isOpen = expandedGroups.includes(group.label);
              return (
                <div key={group.label} style={{ marginBottom: 2 }}>
                  <div onClick={() => toggleGroup(group.label)} style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "8px 16px",
                    cursor: "pointer", userSelect: "none",
                  }}>
                    <span style={{ fontSize: 14 }}>{group.emoji}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: isOpen ? C.accent : C.t2, flex: 1 }}>{group.label}</span>
                    <Icon name={isOpen ? "expand_less" : "expand_more"} size={16} color={C.t3} />
                  </div>
                  {isOpen && group.items.map(item => {
                    const isActive = activeNav === item;
                    return (
                      <div key={item} onClick={() => setActiveNav(item)} style={{
                        display: "flex", alignItems: "center", padding: "8px 10px 8px 22px",
                        marginLeft: 16, marginRight: 8, borderRadius: 6,
                        cursor: "pointer", position: "relative",
                        background: isActive ? C.accentSoft : "transparent",
                        borderLeft: isActive ? `2px solid ${C.accent}` : "2px solid transparent",
                      }}>
                        <span style={{
                          fontSize: 12, fontWeight: isActive ? 600 : 400,
                          color: isActive ? C.accent : C.t2,
                        }}>{item}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </nav>

          {/* User Footer */}
          <div style={{
            padding: "14px 16px", borderTop: `1px solid ${C.divider}`,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%", background: C.accent,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontSize: 11, fontWeight: 700,
            }}>JM</div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>James Mitchell</div>
              <div style={{ fontSize: 10, color: C.t3 }}>General Manager</div>
            </div>
          </div>
        </aside>

        {/* ════════════ MAIN CONTENT ════════════ */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

          {/* ──── Top Bar (56px) ──── */}
          <header style={{
            height: 56, minHeight: 56, background: C.surface, borderBottom: `1px solid ${C.divider}`,
            boxShadow: C.topbarShadow, display: "flex", alignItems: "center", padding: "0 24px",
            justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, color: C.t3 }}>Guests</span>
              <Icon name="chevron_right" size={14} color={C.t3} />
              <span style={{ fontSize: 14, fontWeight: 700, color: C.t1 }}>Customers</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* Date Filter Pills */}
              <div style={{ display: "flex", gap: 2, background: C.hover, borderRadius: 8, padding: 2 }}>
                {datePresets.map(d => (
                  <FilterPill key={d} label={d} active={activeDate === d} onClick={() => setActiveDate(d)} />
                ))}
              </div>

              {/* Compare Toggle */}
              <div style={{
                display: "flex", alignItems: "center", gap: 6, padding: "4px 10px",
                borderRadius: 6, cursor: "pointer", userSelect: "none",
                background: compareMode ? C.accentSoft : "transparent",
              }} onClick={() => setCompareMode(!compareMode)}>
                <Icon name="compare_arrows" size={16} color={compareMode ? C.accent : C.t3} />
                <span style={{ fontSize: 11, fontWeight: 500, color: compareMode ? C.accent : C.t2 }}>Compare</span>
              </div>

              {/* Export */}
              <button style={{
                display: "flex", alignItems: "center", gap: 6, padding: "6px 14px",
                borderRadius: 8, border: `1px solid ${C.cardBorder}`, background: C.surface,
                cursor: "pointer", fontSize: 11.5, color: C.t2, fontWeight: 400,
              }}>
                <Icon name="download" size={15} color={C.t3} />
                Export
              </button>
            </div>
          </header>

          {/* ──── Scrollable Content ──── */}
          <main style={{ flex: 1, overflowY: "auto", padding: 24 }}>

            {/* ═══════ TOP KPI ROW: Retention & Customer Health ═══════ */}
            <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
              {[
                { label: "NEW CUSTOMERS", value: "438", delta: "+12.4%", positive: true, icon: "person_add", sub: "This period" },
                { label: "RETURNING", value: "2,942", delta: "+6.8%", positive: true, icon: "loyalty", sub: "This period" },
                { label: "30-DAY RETURN RATE", value: "27.3%", delta: "+2.1%", positive: true, icon: "replay_30", sub: "Industry avg: 25%" },
                { label: "CHURN RATE", value: "8.4%", delta: "-0.9%", positive: true, icon: "person_off", sub: "60-day threshold" },
                { label: "AVG CLV", value: "$342", delta: "+$18", positive: true, icon: "diamond", sub: "Lifetime value" },
                { label: "AT RISK", value: "356", delta: "+24", positive: false, icon: "warning", sub: "Win-back priority" },
              ].map((kpi, i) => (
                <div key={i} style={{
                  flex: 1, minWidth: 0, background: C.card, borderRadius: 14,
                  border: `1px solid ${C.cardBorder}`, boxShadow: C.shadow,
                  padding: "16px 18px", position: "relative", overflow: "hidden",
                }}>
                  <div style={{
                    position: "absolute", top: -16, right: -16, width: 64, height: 64,
                    borderRadius: "50%", background: kpi.positive ? C.greenSoft : C.redSoft,
                    filter: "blur(16px)", pointerEvents: "none",
                  }} />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ fontSize: 10, fontWeight: 500, color: C.t3, letterSpacing: 0.5, textTransform: "uppercase" }}>{kpi.label}</div>
                    <Icon name={kpi.icon} size={18} color={C.t3} weight={300} />
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.t1, letterSpacing: -0.5, marginTop: 6 }}>{kpi.value}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: kpi.positive ? C.green : C.red, marginTop: 3, display: "flex", alignItems: "center", gap: 3 }}>
                    <Icon name={kpi.positive ? "trending_up" : "trending_down"} size={13} color={kpi.positive ? C.green : C.red} />
                    {kpi.delta}
                  </div>
                  <div style={{ fontSize: 9, color: C.t3, marginTop: 4 }}>{kpi.sub}</div>
                </div>
              ))}
            </div>

            {/* ═══════ ROW 7: RFM Segments ═══════ */}
            <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
              {/* RFM Segment Treemap (simulated) */}
              <SectionCard title="Customer Segments (RFM)" subtitle="3,380 identified customers" icon="pie_chart" flex={1.2}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {rfmSegments.map((seg, i) => {
                    const area = Math.max(60, Math.sqrt(seg.count) * 4.5);
                    return (
                      <div key={i} style={{
                        width: area, height: area, borderRadius: 10, cursor: "pointer",
                        background: `${seg.color}18`, border: `1.5px solid ${seg.color}40`,
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        padding: 6, transition: "all 0.2s",
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background = `${seg.color}28`; e.currentTarget.style.transform = "scale(1.04)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = `${seg.color}18`; e.currentTarget.style.transform = "scale(1)"; }}
                      >
                        <div style={{ fontSize: 16, fontWeight: 700, color: seg.color }}>{seg.count}</div>
                        <div style={{ fontSize: 8, fontWeight: 600, color: seg.color, textAlign: "center", lineHeight: 1.2, marginTop: 2 }}>{seg.name}</div>
                        <div style={{ fontSize: 8, color: C.t3, marginTop: 1 }}>{seg.pct}%</div>
                      </div>
                    );
                  })}
                </div>
              </SectionCard>

              {/* RFM Summary Table */}
              <SectionCard title="Segment Breakdown" subtitle="Key metrics per segment" icon="table_chart" flex={1.4}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${C.divider}` }}>
                      {["SEGMENT", "CUSTOMERS", "% OF TOTAL", "AVG CLV", "AVG VISITS"].map(h => (
                        <th key={h} style={{ fontSize: 10, fontWeight: 600, color: C.t3, textAlign: "left", padding: "6px 10px", textTransform: "uppercase", letterSpacing: 0.3 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rfmSegments.map((seg, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.divider}`, cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.background = C.hover}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <td style={{ padding: "8px 10px" }}>
                          <SegmentBadge label={seg.name} color={seg.color} />
                        </td>
                        <td style={{ padding: "8px 10px", fontSize: 12, fontWeight: 500, color: C.t1 }}>{seg.count.toLocaleString()}</td>
                        <td style={{ padding: "8px 10px", fontSize: 12, color: C.t2 }}>{seg.pct}%</td>
                        <td style={{ padding: "8px 10px", fontSize: 12, fontWeight: 600, color: C.t1 }}>{seg.clv}</td>
                        <td style={{ padding: "8px 10px", fontSize: 12, color: C.t1 }}>{seg.avgVisits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </SectionCard>
            </div>

            {/* ═══════ ROW 9: Cohort Retention Heatmap ═══════ */}
            <SectionCard title="Cohort Retention" subtitle="Monthly cohort return rates" icon="grid_on" style={{ marginBottom: 24 }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 3 }}>
                  <thead>
                    <tr>
                      <th style={{ fontSize: 10, fontWeight: 600, color: C.t3, textAlign: "left", padding: "4px 8px", minWidth: 80 }}>COHORT</th>
                      {["Month 0", "Month 1", "Month 2", "Month 3", "Month 4", "Month 5"].map(m => (
                        <th key={m} style={{ fontSize: 9, fontWeight: 500, color: C.t3, textAlign: "center", padding: "4px 6px", minWidth: 64 }}>{m}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cohortData.map((row, ri) => (
                      <tr key={ri}>
                        <td style={{ fontSize: 11, fontWeight: 500, color: C.t2, padding: "4px 8px" }}>{row.cohort}</td>
                        {[row.m0, row.m1, row.m2, row.m3, row.m4, row.m5].map((val, ci) => (
                          <td key={ci} style={{
                            textAlign: "center", padding: 0,
                          }}>
                            <div style={{
                              background: getCohortColor(val), borderRadius: 4,
                              padding: "8px 4px", fontSize: 11, fontWeight: val !== null ? 600 : 400,
                              color: val !== null ? (val >= 30 ? "#fff" : C.t1) : C.t3,
                              transition: "all 0.15s",
                            }}>
                              {val !== null ? `${val}%` : "—"}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 12, alignItems: "center" }}>
                <span style={{ fontSize: 10, color: C.t3 }}>Retention:</span>
                {[
                  { label: "30%+", bg: C.green },
                  { label: "20–30%", bg: "rgba(22,163,74,0.55)" },
                  { label: "15–20%", bg: "rgba(22,163,74,0.35)" },
                  { label: "10–15%", bg: "rgba(22,163,74,0.2)" },
                  { label: "<10%", bg: "rgba(22,163,74,0.08)" },
                ].map(l => (
                  <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 14, height: 10, borderRadius: 2, background: l.bg }} />
                    <span style={{ fontSize: 9, color: C.t3 }}>{l.label}</span>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* ═══════ ROW 10: Top Customers Table ═══════ */}
            <SectionCard title="Top Customers" subtitle="Highest lifetime spend" icon="workspace_premium"
              style={{ marginBottom: 24 }}
              rightAction={
                <div style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "5px 12px",
                  border: `1px solid ${C.cardBorder}`, borderRadius: 6, background: C.surface,
                }}>
                  <Icon name="search" size={15} color={C.t3} />
                  <input
                    placeholder="Search customers..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{
                      border: "none", outline: "none", background: "transparent",
                      fontSize: 11, color: C.t1, width: 140,
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  />
                </div>
              }
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.divider}` }}>
                    {["#", "CUSTOMER", "VISITS", "TOTAL SPEND", "LAST VISIT", "SEGMENT", "PRIMARY CHANNEL"].map(h => (
                      <th key={h} style={{
                        fontSize: 10, fontWeight: 600, color: C.t3, textAlign: "left",
                        padding: "8px 12px", textTransform: "uppercase", letterSpacing: 0.3,
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topCustomers
                    .filter(c => !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()))
                    .map((c, i) => {
                    const seg = rfmSegments.find(s => s.name === c.segment || s.name.startsWith(c.segment));
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.divider}`, cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.background = C.hover}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <td style={{ padding: "10px 12px" }}>
                          <span style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 20, height: 20, borderRadius: 4, fontSize: 10, fontWeight: 700,
                            background: i === 0 ? C.accentSoft : C.hover,
                            color: i === 0 ? C.accent : C.t3,
                          }}>{i + 1}</span>
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{
                              width: 28, height: 28, borderRadius: "50%",
                              background: `${CHANNEL_COLORS[i % CHANNEL_COLORS.length]}18`,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 11, fontWeight: 600, color: CHANNEL_COLORS[i % CHANNEL_COLORS.length],
                            }}>
                              {c.name.split(" ").map(n => n[0]).join("")}
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 500, color: C.t1 }}>{c.name}</span>
                          </div>
                        </td>
                        <td style={{ padding: "10px 12px", fontSize: 12, color: C.t1 }}>{c.visits}</td>
                        <td style={{ padding: "10px 12px", fontSize: 12, fontWeight: 600, color: C.t1 }}>{c.spend}</td>
                        <td style={{ padding: "10px 12px", fontSize: 12, color: C.t2 }}>{c.lastVisit}</td>
                        <td style={{ padding: "10px 12px" }}>
                          <SegmentBadge label={c.segment} color={seg?.color || C.t3} />
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <span style={{ fontSize: 11, color: C.t2, display: "flex", alignItems: "center", gap: 4 }}>
                            <Icon name={c.channel === "Dine-In" ? "restaurant" : c.channel === "Pickup" ? "shopping_bag" : c.channel === "Delivery" ? "local_shipping" : "phone_android"} size={14} color={C.t3} />
                            {c.channel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </SectionCard>

            {/* ═══════ ROW 11: Churn Alert Banner ═══════ */}
            <div style={{
              background: C.orangeSoft, border: `1px solid ${C.orange}33`, borderRadius: 12,
              padding: "14px 20px", display: "flex", alignItems: "center", gap: 14,
              marginBottom: 24,
            }}>
              <Icon name="warning" size={22} color={C.orange} fill={1} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.t1 }}>
                  356 customers are at risk of churning
                </div>
                <div style={{ fontSize: 11, color: C.t2, marginTop: 2 }}>
                  24 more than last period. Top at-risk: Lisa Thompson (14 visits, $1,820 CLV, last seen 10 days ago — 1.5× her usual gap).
                </div>
              </div>
              <button style={{
                padding: "6px 14px", borderRadius: 6, border: `1px solid ${C.orange}44`,
                background: "transparent", cursor: "pointer", fontSize: 11, fontWeight: 600,
                color: C.orange, whiteSpace: "nowrap",
              }}>
                View At-Risk List
              </button>
            </div>

          </main>
        </div>
      </div>
    </>
  );
}
